import { makeAutoObservable } from 'mobx';
import { chatCredentialsStore } from '../ChatCredentialsStore';
import { chatStore } from '../ChatStore';
import { logger } from '@/utils/logger';

/**
 * 登录状态管理器
 * 负责处理登录状态和needReLogin逻辑
 * 遵循单一职责原则：专门处理登录状态管理
 */
class LoginStateManager {
  constructor() {
    makeAutoObservable(this);

    // 登录状态
    this.isLoggedIn = false;
    this.needReLogin = false;
    this.currentServiceUserId = null;
    this.currentConversationId = null;
    this.lastLoginTimestamp = null;

    // 缓存信息
    this.cachedLoginInfo = null;
  }

  /**
   * 设置登录状态
   */
  setLoggedIn = status => {
    this.isLoggedIn = status;
  };

  /**
   * 设置需要重新登录状态
   */
  setNeedReLogin = status => {
    this.needReLogin = status;
  };

  /**
   * 设置当前服务用户ID
   */
  setCurrentServiceUserId = userId => {
    this.currentServiceUserId = userId;
  };

  /**
   * 设置当前会话ID
   */
  setCurrentConversationId = conversationId => {
    this.currentConversationId = conversationId;
  };

  /**
   * 缓存登录信息
   */
  cacheLoginInfo = (tenantId, loginInfo) => {
    this.cachedLoginInfo = {
      tenantId,
      virtualServiceUserId: loginInfo.virtualServiceUserId,
      conversationId: loginInfo.conversationId,
      timestamp: Date.now(),
    };
  };

  /**
   * 获取缓存的登录信息
   */
  getCachedLoginInfo = tenantId => {
    if (!this.cachedLoginInfo || this.cachedLoginInfo.tenantId !== tenantId) {
      return null;
    }

    // 检查缓存是否过期（24小时）
    const now = Date.now();
    const cacheAge = now - this.cachedLoginInfo.timestamp;
    const maxAge = 24 * 60 * 60 * 1000; // 24小时

    if (cacheAge > maxAge) {
      this.cachedLoginInfo = null;
      return null;
    }

    return this.cachedLoginInfo;
  };

  /**
   * 清除缓存
   */
  clearCache = () => {
    this.cachedLoginInfo = null;
  };

  /**
   * 更新登录凭证到ChatCredentialsStore
   */
  updateCredentials = credentials => {
    if (credentials) {
      chatCredentialsStore.setCredentials(credentials);
      logger.info('[LoginStateManager] 更新登录凭证到ChatCredentialsStore:', {
        sdkAppId: credentials.sdkAppId,
        userId: credentials.userId,
        hasUserSig: !!credentials.userSig,
      });
    }
  };

  /**
   * 处理登录流程
   * @param {Object} params - 登录参数
   * @param {string} params.chatUserId - 聊天用户ID
   * @param {string} params.tenantId - 租户ID
   * @param {Object} [params.existingStatusResult] - 已有的服务状态结果（避免重复请求）
   * @param {Object} [params.existingCredentials] - 已有的登录凭证（避免重复请求）
   * @returns {Promise<Object>} 登录结果
   */
  async handleLoginFlow(params) {
    try {
      logger.info('[LoginStateManager] 开始处理登录流程:', params);

      // 1. 获取服务状态结果
      let statusResult;
      if (params.existingStatusResult) {
        logger.info('[LoginStateManager] 使用已有的服务状态结果，跳过重复请求');
        statusResult = params.existingStatusResult;
      } else {
        logger.info('[LoginStateManager] 没有已有的服务状态结果，尝试从ChatStore获取');

        // 优先从ChatStore获取已有的服务状态结果
        const existingResult = chatStore.getServiceStatusResult();
        if (existingResult) {
          logger.info('[LoginStateManager] 从ChatStore获取到已有的服务状态结果');
          statusResult = existingResult;
        } else {
          logger.info('[LoginStateManager] ChatStore中没有已有的服务状态结果，抛出错误');
          throw new Error('ChatStore中没有服务状态结果，请先调用joinChat获取服务状态');
        }
      }

      // 更新状态
      this.setNeedReLogin(statusResult.needReLogin);
      this.setCurrentConversationId(statusResult.conversation?.id);

      if (statusResult.virtualServiceUser) {
        this.setCurrentServiceUserId(statusResult.virtualServiceUser.id);
      }

      // 2. 获取登录凭证 - 优化：优先使用已有的凭证数据
      let credentials;
      if (params.existingCredentials) {
        logger.info('[LoginStateManager] 使用已有的登录凭证，跳过重复请求');
        credentials = params.existingCredentials;
      } else {
        logger.info('[LoginStateManager] 没有已有的登录凭证，需要重新获取');

        // 验证必要的参数
        if (!statusResult.virtualServiceUser?.id) {
          throw new Error('缺少虚拟客服用户ID，无法获取登录凭证');
        }

        if (!statusResult.conversation?.id) {
          throw new Error('缺少会话ID，无法获取登录凭证');
        }

        credentials = await chatStore.getVirtualServiceCredentials({
          serviceUserId: statusResult.virtualServiceUser.id,
          conversationId: statusResult.conversation.id,
        });
      }

      // 3. 更新登录凭证到ChatCredentialsStore
      this.updateCredentials(credentials);

      // 4. 如果需要重新登录，缓存登录信息
      if (statusResult.needReLogin) {
        logger.info('[LoginStateManager] 需要重新登录，缓存登录信息');

        this.cacheLoginInfo(params.tenantId, {
          virtualServiceUserId: statusResult.virtualServiceUser.id,
          conversationId: statusResult.conversation.id,
        });
      }

      // 5. 更新登录状态
      this.setLoggedIn(true);
      this.lastLoginTimestamp = Date.now();

      return {
        ...statusResult,
        credentials, // 总是返回credentials
      };
    } catch (error) {
      logger.error('[LoginStateManager] 登录流程处理失败:', error);
      this.setLoggedIn(false);
      throw error;
    }
  }

  /**
   * 处理注销
   * @param {string} reason - 注销原因
   */
  handleLogout = (reason = 'manual') => {
    logger.info('[LoginStateManager] 处理注销:', reason);

    // 清除登录状态
    this.setLoggedIn(false);
    this.setNeedReLogin(false);
    this.setCurrentServiceUserId(null);
    this.setCurrentConversationId(null);
    this.lastLoginTimestamp = null;

    // 清除缓存
    this.clearCache();

    // 清除凭证
    chatCredentialsStore.reset();

    logger.info('[LoginStateManager] 注销完成');
  };

  /**
   * 重置状态
   */
  reset = () => {
    this.isLoggedIn = false;
    this.needReLogin = false;
    this.currentServiceUserId = null;
    this.currentConversationId = null;
    this.lastLoginTimestamp = null;
    this.clearCache();
  };
}

export const loginStateManager = new LoginStateManager();
